/**
 * FondoMapa — el plano de ciudad sobre el que se dibuja la cuadrícula.
 *
 * ── QUÉ HACÍA FALSA A LA VERSIÓN ANTERIOR ───────────────────────────────────
 *
 * Eran seis rectángulos: tres barras horizontales y tres verticales, todas a
 * sangre y repartidas casi a intervalos iguales, más cuatro bloques de color.
 * Ninguna ciudad se parece a eso. Lo que delata un plano inventado no es el
 * color, es la REGULARIDAD: calles que cruzan el mapa entero, ángulos rectos
 * en todo, y espaciado uniforme.
 *
 * Así que esto va en SVG y no en `<div>`s, que es lo que permite las tres cosas
 * que lo arreglan:
 *
 *   1. **Diagonales y curvas.** Una avenida en diagonal y una ronda curva es lo
 *      primero que hace que un plano parezca un plano.
 *   2. **Calles que empiezan y acaban.** Las secundarias mueren contra otra vía
 *      o contra el río, no siguen hasta el borde.
 *   3. **Manzanas de tamaños distintos.** Un damero perfecto se lee como una
 *      hoja de cálculo.
 *
 * Los colores son los de un mapa de verdad: la tierra tirando a hueso, el agua
 * en azul grisáceo, los parques en verde apagado y las vías principales en el
 * ámbar que usa Google para las carreteras. Nada saturado — esto va DEBAJO de
 * veinticinco discos de colores y su trabajo es no competir con ellos.
 *
 * Decorativo por completo: quien lo monta ya pone el `aria-hidden`.
 */
export default function FondoMapa({ className = '' }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      focusable="false"
    >
      {/* Tierra */}
      <rect width="100" height="100" fill="#eef0ea" />

      {/* Río, en diagonal y con curva: es lo que rompe la cuadrícula y lo que
          más «mapa» aporta por píxel dibujado. */}
      <path
        d="M-6 88 C 12 85, 24 95, 40 99 C 54 103, 66 110, 72 118 L 100 118 L 100 126 L -6 126 Z"
        fill="#c5dcea"
      />
      <path
        d="M-6 88 C 12 85, 24 95, 40 99 C 54 103, 66 110, 72 118"
        fill="none"
        stroke="#b3cfe0"
        strokeWidth="0.6"
      />

      {/* Parques, con contornos irregulares. */}
      <path d="M62 2 L86 5 L90 19 L78 26 L63 20 Z" fill="#d7e6cd" />
      <path d="M8 40 L21 38 L24 49 L12 53 L6 47 Z" fill="#d7e6cd" />

      {/* Manzanas. Tamaños y huecos deliberadamente desiguales. */}
      <g fill="#e1ded5">
        <rect x="4" y="6" width="13" height="9" />
        <rect x="20" y="4" width="9" height="11" />
        <rect x="32" y="7" width="15" height="8" />
        <rect x="4" y="20" width="10" height="13" />
        <rect x="18" y="21" width="16" height="10" />
        <rect x="38" y="22" width="8" height="12" />
        <rect x="52" y="30" width="14" height="9" />
        <rect x="70" y="33" width="11" height="14" />
        <rect x="86" y="30" width="10" height="9" />
        <rect x="30" y="40" width="12" height="11" />
        <rect x="48" y="46" width="9" height="8" />
        <rect x="64" y="54" width="16" height="10" />
        <rect x="86" y="50" width="10" height="16" />
        <rect x="24" y="60" width="11" height="9" />
        <rect x="40" y="62" width="14" height="8" />
        <rect x="58" y="72" width="12" height="11" />
        <rect x="76" y="76" width="14" height="9" />
        <rect x="8" y="62" width="9" height="7" />
      </g>

      {/* Calles secundarias: blancas, finas, y varias acaban a media altura. */}
      <g stroke="#ffffff" strokeLinecap="round" fill="none">
        <path d="M0 17.5 L58 17.5" strokeWidth="1.5" />
        <path d="M0 36 L47 36" strokeWidth="1.2" />
        <path d="M30 52 L100 52" strokeWidth="1.5" />
        <path d="M20 70 L100 70" strokeWidth="1.2" />
        <path d="M46 0 L46 46" strokeWidth="1.5" />
        <path d="M16 0 L16 62" strokeWidth="1.2" />
        <path d="M84 24 L84 100" strokeWidth="1.5" />
        <path d="M60 28 L60 52" strokeWidth="1" />
        <path d="M70 52 L70 100" strokeWidth="1" />
        <path d="M36 52 L36 78" strokeWidth="1" />
      </g>

      {/* Avenida principal: diagonal y en ámbar, como las vías rápidas de
          Google. Es la que ata el conjunto. */}
      <path
        d="M-4 26 L44 44 L72 44 L104 60"
        fill="none"
        stroke="#fbeccd"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M-4 26 L44 44 L72 44 L104 60"
        fill="none"
        stroke="#f2dfb4"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.45"
        style={{ mixBlendMode: 'multiply' }}
      />

      {/* Ronda: una curva amplia por el noreste. */}
      <path
        d="M52 -4 C 74 6, 92 16, 99 38"
        fill="none"
        stroke="#fbeccd"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
